import {Injectable} from '@angular/core';
import {Observable, distinctUntilChanged, map} from 'rxjs';
import {Store} from './store.model';
import {
    BlockActivityInfo,
    BlockActivityInfoUpdatedBy,
    MissedAtStopEvent,
    PassedPosition,
    StopInfo,
    VehicleAtStopEvent,
    VehicleHistory,
    VehicleHistoryEvent
} from '../../shared/models/vehicle-history';

import * as _ from 'lodash';
import * as turf from '@turf/turf';
import moment from 'moment';


export interface VehicleHistoryEventChange {
    eventSelectorId: string;
}

export interface HistoryStoreState {
    vehicleHistory: VehicleHistory;
    highlightedTime: PassedPosition;
    selectedActivity?: BlockActivityInfo;
    mapOrScrubbarChange?: VehicleHistoryEventChange;
}

@Injectable({
    providedIn: 'root'
})
export class HistoryStore extends Store<HistoryStoreState> {
    constructor() {
        super({
            vehicleHistory: undefined,
            highlightedTime: undefined,
            selectedActivity: undefined,
            mapOrScrubbarChange: undefined
        });
    }

    public get hasPositions() {
        return this.stateSnapshot?.vehicleHistory?.passedPositions;
    }

    public getPropertyState<K extends keyof HistoryStoreState>(propertyKey: K):
        Observable<VehicleHistory | PassedPosition | BlockActivityInfo | VehicleHistoryEventChange> {
        return super.state$.pipe(
            map(state => state[propertyKey]),
            distinctUntilChanged()
        );
    }

    public resetData() {
        this.setState({
            ...this.stateSnapshot,
            vehicleHistory: undefined,
            highlightedTime: undefined
        });
    }

    public updateHistoryData(vehicleHistory: VehicleHistory) {
        this.setState({
            ...this.stateSnapshot,
            vehicleHistory
        });
    }

    /**
     * Update history state based on a PassedPosition
     *
     * @param highlightedTime - The PassedPosition
     * @param changedByMapOrScrubbarClick - If the scrollToEventSelectorId state should be updated (if all requirements are met, that is)
     */
    public updateHighlightedTime(highlightedTime: PassedPosition, changedByMapOrScrubbarClick = false) {
        // eslint-disable-next-line max-len
        this._updateActiveEventStatus(highlightedTime.ts, this.stateSnapshot.selectedActivity?.events, changedByMapOrScrubbarClick);
        this.setState({
            ...this.stateSnapshot,
            highlightedTime
        });
    }

    /**
     * Set selectedActivity based on highlightedTime
     */
    public selectActivityBasedOnPosition(highlightedTime: PassedPosition, updatedBy = BlockActivityInfoUpdatedBy.else) {
        const selectedActivity = this._findActivityBasedOnPosition(highlightedTime);
        if (selectedActivity) {
            selectedActivity.updatedBy = updatedBy;
        }

        this.selectActivity(selectedActivity);
    }

    /**
     * Set selectedActivity state
     */
    public selectActivity(activity: BlockActivityInfo) {
        this.setState({
           ...this.stateSnapshot,
           selectedActivity: activity
        });
    }

    public selectFirstPositionOfActivity(activity: BlockActivityInfo, changedByMapOrScrubbarClick = false) {
        const firstPosition = activity?.positionTimestamps[0];
        const position = this.stateSnapshot?.vehicleHistory?.passedPositionsMap.get(firstPosition);
        if (position == undefined) {
            return;
        }

        this.updateHighlightedTime(position, changedByMapOrScrubbarClick);
    }

    public highlightByTimeStampString(ts: string, updatedBy = BlockActivityInfoUpdatedBy.else) {
        const history = this.stateSnapshot?.vehicleHistory;
        if (!history) {
            return;
        }

        const position = history.passedPositionsMap.get(ts);
        if (!position) {
            return;
        }
        this.selectActivityBasedOnPosition(position, updatedBy);
        this.updateHighlightedTime(position, false);
    }

    public highlightByCoordinate(lat: number, lon: number) {
        const vehicleHistory = this.stateSnapshot?.vehicleHistory;
        if (!vehicleHistory?.passedPositions || vehicleHistory.passedPositions.length === 0) {
            console.warn('Cannot highlight by coordinate: no vehicle history or passed positions available');
            return;
        }

        const coordinates = _.map(vehicleHistory.passedPositions, vh => [vh.long, vh.lat]);
        if (coordinates.length < 2) {
            console.warn('Cannot highlight by coordinate: insufficient coordinates for line creation');
            return;
        }

        const line = turf.lineString(coordinates);
        const clickedPoint = turf.point([lon, lat]);
        const pointOnLine = turf.nearestPointOnLine(line, clickedPoint);

        const min = _.minBy(coordinates, coord => turf.distance(turf.point(coord), pointOnLine));

        const positions = vehicleHistory.passedPositions;
        const matchingPassedPosition = _.find(positions, pos => pos.long === min[0] && pos.lat === min[1]);
        
        if (!matchingPassedPosition) {
            console.warn('Cannot highlight by coordinate: no matching position found');
            return;
        }
        
        this.selectActivityBasedOnPosition(matchingPassedPosition, BlockActivityInfoUpdatedBy.mapCoordinateClick);
        this.updateHighlightedTime(matchingPassedPosition, true);
    }

    private _findActivityBasedOnPosition(highlightedTime: PassedPosition) {
        const blockActivities = this.stateSnapshot.vehicleHistory?.blockActivityInfo || [];
        return _.find(blockActivities, (a: BlockActivityInfo) => moment(highlightedTime.ts).isBetween(a.from,a.to, 'second' , '[]'));
    }

    /**
     * Set the Vehicle History Event, corresponding to the current timestamp of the history playback, to be highlighted.
     *
     * @param highlightedTimestampDate - The current timestamp of the history playback
     * @param vehicleHistoryEvents - The Vehicle History Events
     * @param changedByMapOrScrubbarClick - If the scrollToEventSelectorId state should be updated (if all requirements are met, that is)
     * @private
     */
    // eslint-disable-next-line max-len
    private _updateActiveEventStatus(highlightedTimestampDate: Date, vehicleHistoryEvents: VehicleHistoryEvent[], changedByMapOrScrubbarClick: boolean): void {
        if (vehicleHistoryEvents == undefined || vehicleHistoryEvents.length === 0) {
            return;
        }

        const timestamp = moment(highlightedTimestampDate);

        // Create a list with all the timestamps of the History Events
        const eventTimeValues: { eventIndex: number; date: Date }[] = [];
        vehicleHistoryEvents.forEach((vehicleHistoryEvent, index) => {
            if (vehicleHistoryEvent.type === 'StopEventInfo') {
                const stopInfoEvent = vehicleHistoryEvent as StopInfo;
                eventTimeValues.push({eventIndex: index, date: stopInfoEvent.from});
                eventTimeValues.push({eventIndex: index, date: stopInfoEvent.to});

            } else if (vehicleHistoryEvent.type === 'MissedAtStopEvent' || vehicleHistoryEvent.type === 'VehicleAtStopEvent') {
                const missedOrVehicleAtStopEvent = vehicleHistoryEvent as MissedAtStopEvent | VehicleAtStopEvent;
                eventTimeValues.push({eventIndex: index, date: missedOrVehicleAtStopEvent.plannedArrival});
                eventTimeValues.push({eventIndex: index, date: missedOrVehicleAtStopEvent.plannedDeparture});

            } else {
                eventTimeValues.push({eventIndex: index, date: vehicleHistoryEvent.ts});
            }
        });

        // Use the list of History Event timestamps to find the event closest to the highlighted timestamp
        const closestDate = eventTimeValues.reduce((closest, current) => {
            const currentMoment = moment(current.date);
            const closestMoment = moment(closest.date);
            const diffCurrent = Math.abs(currentMoment.diff(timestamp));
            const diffClosest = Math.abs(closestMoment.diff(timestamp));
            return diffCurrent < diffClosest ? current : closest;
        });

        let prevActiveEventSelectorId: string;

        // Disable highlight for all events of all BlobActivityInfos
        const blockActivityInfos = this.stateSnapshot.vehicleHistory.blockActivityInfo;
        for (const blockActivityInfo of blockActivityInfos) {
            for (const event of blockActivityInfo.events) {
                if (event.isActive){
                    prevActiveEventSelectorId = event.selectorId;
                }

                event.isActive = false;
            }
        }

        // Set highlight flag for the current event
        if (closestDate.eventIndex < vehicleHistoryEvents.length) {
            const currEvent = vehicleHistoryEvents[closestDate.eventIndex];
            currEvent.isActive = true;

            if (prevActiveEventSelectorId == undefined || prevActiveEventSelectorId === currEvent.selectorId) {
                return;
            }

            // Send out a signal to scroll to an event if a new event has become highlighted, and the shouldScrollToEvent flag is true
            if (changedByMapOrScrubbarClick) {
                this.setState({
                    ...this.stateSnapshot,
                    mapOrScrubbarChange: {eventSelectorId: currEvent.selectorId}
                });
            }
        }
    }
}
